/**
 * Audit page section 2: levers. Levers 1 and 2 are calculated by the engine;
 * levers 3 to 5 are worked by hand at launch and their outcome is entered here.
 */
import {
  ENGINE_VERSION,
  annualise,
  combineLevers12,
  csvTemplate,
  productSpecsFromRows,
  runLever1,
  runLever2,
  type Lever1ParcelResult,
  type Lever1Summary,
  type Lever2ParcelResult,
  type ProductSpec,
} from "@ryoma/engine";
import type { AuditRow, Db, LeverRun, Lever2RunSummary, ManualLever, UploadRow } from "../data/db.ts";
import { loadTable } from "../lib/files.ts";
import { LEVERS, cardRef, latestCards } from "../lib/levers.ts";
import { registerAuditSection } from "./audit.ts";
import { action, dropZone, date, gbp, h, mount, notice, num, pct, stat, table } from "./dom.ts";
import { downloadLink } from "./reference.ts";

const MAX_STORED = 2000;

const FLAG_LABEL: Record<string, string> = {
  overcharged: "Overcharged",
  undercharged: "Charged below rate card",
  volumetric_applies: "Volumetric weight",
  near_cheaper_band: "Just over a cheaper band",
  no_band_fits: "Too big for every band",
  unknown_service: "Service not priced",
};

const REASON_LABEL: Record<string, string> = {
  multi_item: "More than one product (manual review)",
  no_items: "No SKU in the data",
  missing_product_spec: "No product size for this SKU",
  unknown_service: "Service not priced",
  current_not_priced: "Current parcel fits no band",
  no_format_fits: "No format in the catalogue fits",
};

function annual(pence: number, upload: UploadRow): string {
  const a = annualise(pence, upload.date_from, upload.date_to);
  return a === null ? "period too short to scale" : `about ${gbp(a)} a year`;
}

function staleNotice(run: LeverRun | undefined, upload: UploadRow, cardsNow: string[]): HTMLElement | "" {
  if (!run) return "";
  if (run.dataset_hash !== upload.dataset_hash) return notice("warn", "The client data has changed since this was run. Run it again.");
  if (run.rate_cards.join() !== cardsNow.join()) return notice("warn", "Rate cards have changed since this was run. Run it again to use the newest prices.");
  if (run.engine_version !== ENGINE_VERSION) return notice("info", `Run with engine ${run.engine_version}; the current engine is ${ENGINE_VERSION}.`);
  return "";
}

function lever1Results(run: LeverRun, upload: UploadRow): HTMLElement[] {
  const s = run.summary as Lever1Summary;
  const rows = (run.results ?? []) as Lever1ParcelResult[];
  const top = [...rows]
    .filter((r) => r.flags.includes("overcharged") || r.nearBand)
    .sort((a, b) => Math.max(b.differencePence ?? 0, 0) + (b.nearBand?.savingPence ?? 0) - (Math.max(a.differencePence ?? 0, 0) + (a.nearBand?.savingPence ?? 0)))
    .slice(0, 15);
  return [
    h("div", { class: "stats" },
      stat("Parcels priced", `${num(s.priced)} of ${num(s.parcels)}`, pct(s.priced, s.parcels)),
      stat("Charged", gbp(s.chargedPence), `rate card price ${gbp(s.expectedPence)}`),
      stat("Overcharged", gbp(s.overchargePence), `${num(s.overchargedCount)} parcels · ${annual(s.overchargePence, upload)}`, s.overchargePence > 0 ? "good" : undefined),
      stat("Just over a cheaper band", gbp(s.nearBandSavingPence), `${num(s.nearBandCount)} parcels · ${annual(s.nearBandSavingPence, upload)}`, s.nearBandSavingPence > 0 ? "good" : undefined),
      stat("Volumetric weight charged", num(s.volumetricCount), "parcels charged on size, not weight"),
    ),
    s.unknownServiceCount + s.noBandCount > 0
      ? notice("warn", `${num(s.unknownServiceCount)} parcels have a service not on any rate card and ${num(s.noBandCount)} fit no band; they are left out of the totals.`)
      : "",
    h("h4", null, "By band"),
    table(["Service / band", "Parcels", "Charged", "Rate card price"],
      Object.entries(s.byBand).sort((a, b) => b[1].count - a[1].count).map(([k, v]) => [k, num(v.count), gbp(v.chargedPence), gbp(v.expectedPence)]),
      { numeric: [1, 2, 3] }),
    top.length
      ? [h("h4", null, "Largest savings"),
         table(["Order", "Band", "Charged", "Rate card", "Finding", "Cheaper band"],
           top.map((r) => [r.orderId, r.bandCode ?? "–", gbp(r.chargedPence), gbp(r.expectedPence),
             r.flags.map((f) => FLAG_LABEL[f] ?? f).join(", "),
             r.nearBand ? `${r.nearBand.bandName}: saves ${gbp(r.nearBand.savingPence)}, over by ${Object.entries(r.nearBand.overBy).map(([k, v]) => `${v}${k.endsWith("Kg") ? " kg" : " cm"} ${k.replace(/(Cm|Kg)$/, "")}`).join(", ")}` : "–"]),
           { numeric: [2, 3] })]
      : "",
  ].flat() as HTMLElement[];
}

function lever2Results(run: LeverRun, upload: UploadRow): HTMLElement[] {
  const s = run.summary as Lever2RunSummary;
  const rows = (run.results ?? []) as Lever2ParcelResult[];
  const top = [...rows].sort((a, b) => (b.netSavingPence ?? b.postageSavingPence) - (a.netSavingPence ?? a.postageSavingPence)).slice(0, 15);
  const saving = s.netSavingPence ?? s.postageSavingPence;
  return [
    h("div", { class: "stats" },
      stat("Parcels assessed", `${num(s.assessed)} of ${num(s.parcels)}`, pct(s.assessed, s.parcels)),
      stat("Could change format", num(s.savingCount), pct(s.savingCount, s.assessed)),
      stat("Postage saving", gbp(s.postageSavingPence), annual(s.postageSavingPence, upload), s.postageSavingPence > 0 ? "good" : undefined),
      stat("Net of packaging cost", s.netSavingPence === null ? "–" : gbp(s.netSavingPence), s.netSavingPence === null ? "enter current packaging cost" : annual(s.netSavingPence, upload)),
    ),
    h("div", { class: "card combined" },
      h("strong", null, "Levers 1 and 2 together: "), gbp(s.combined.totalPence), " in the data period, ", annual(s.combined.totalPence, upload), ".",
      h("p", { class: "muted small" }, `Overcharges ${gbp(s.combined.overchargePence)} plus band savings ${gbp(s.combined.bandSavingPence)} across ${num(s.combined.parcelsWithSaving)} parcels. A parcel that both levers would move to a cheaper band is counted once.`),
    ),
    Object.keys(s.notAssessed).length
      ? [h("h4", null, "Not assessed"), table(["Reason", "Parcels"], Object.entries(s.notAssessed).map(([k, v]) => [REASON_LABEL[k] ?? k, num(v)]), { numeric: [1] })]
      : "",
    Object.keys(s.byFormat).length
      ? [h("h4", null, "Proposed formats"), table(["Format", "Parcels"], Object.entries(s.byFormat).sort((a, b) => b[1] - a[1]).map(([k, v]) => [k, num(v)]), { numeric: [1] })]
      : "",
    top.length
      ? [h("h4", null, "Largest savings"),
         table(["Order", "SKU", "Now", "Proposed", "Postage", "Packaging", "Saving"],
           top.map((r) => [r.orderId, r.sku ?? "", `${r.currentBandCode} ${gbp(r.currentPostagePence)}`,
             r.proposed ? `${r.proposed.formatName} → ${r.proposed.bandCode}` : "–",
             gbp(r.proposed?.postagePence), gbp(r.proposed?.packagingPence), gbp(r.netSavingPence ?? r.postageSavingPence)]),
           { numeric: [4, 5, 6] })]
      : "",
  ].flat() as HTMLElement[];
}

async function leversSection({ db, audit, upload, refresh }: { db: Db; audit: AuditRow; upload: UploadRow | null; refresh: () => void }): Promise<HTMLElement> {
  const box = h("div", { class: "card" }, h("h2", null, "2. Levers"));
  if (!upload) {
    box.append(notice("info", "Upload client data first."));
    return box;
  }
  const [cardRows, cats, runs, specs, manual] = await Promise.all([
    db.listRateCards(), db.listCatalogues(), db.latestLeverRuns(audit.id), db.productSpecs(audit.id), db.manualLevers(audit.id),
  ]);
  const cards = latestCards(cardRows);
  const cardsNow = cards.map(cardRef);
  const sample = cards.some((c) => c.source.startsWith("SAMPLE"));

  // ---- Lever 1
  const l1 = runs[1];
  const l1Current = !!l1 && l1.dataset_hash === upload.dataset_hash;
  const run1 = action(l1 ? "Run lever 1 again" : "Run lever 1", async (_b, status) => {
    if (cards.length === 0) throw new Error("Add a rate card first (Rate cards & packaging)");
    status.textContent = "Loading parcels…";
    const parcels = await db.uploadParcels(upload.id);
    const { results, summary } = runLever1(parcels, cards.map((c) => c.body));
    await db.saveLeverRun({
      audit_id: audit.id, upload_id: upload.id, lever: 1, engine_version: ENGINE_VERSION, dataset_hash: upload.dataset_hash,
      rate_cards: cardsNow, packaging_catalogue: null, options: {}, summary,
      results: results.filter((r) => r.flags.length > 0).slice(0, MAX_STORED),
    });
    refresh();
  }, { primary: !l1 });

  // ---- Lever 2 inputs
  const specFile = h("input", { type: "file", accept: ".csv,.xlsx", "aria-label": "Product sizes file" });
  const specOut = h("div");
  const saveSpecs = action("Upload product sizes", async () => {
    const f = specFile.files?.[0];
    if (!f) throw new Error("Choose a CSV or Excel file");
    const { value, problems } = productSpecsFromRows((await loadTable(f)).rows);
    if (!value) {
      mount(specOut, notice("error", h("strong", null, "Not saved: "), problems.slice(0, 10).join("; ")));
      return;
    }
    await db.replaceProductSpecs(audit.id, value);
    refresh();
  });
  const catSel = h("select", { "aria-label": "Packaging catalogue" }, cats.map((c, i) => h("option", { value: cardRef(c), selected: i === 0 }, `${cardRef(c)} (${c.body.formats.length} formats)`)));
  const packCost = h("input", { type: "number", min: "0", step: "0.01", placeholder: "e.g. 0.55", "aria-label": "Current packaging cost per parcel (£)" , class: "input-short" });
  const prev = runs[2]?.options as { currentPackagingPence?: number | null } | undefined;
  if (prev?.currentPackagingPence != null) packCost.value = (prev.currentPackagingPence / 100).toFixed(2);
  const l2Ready = l1Current && specs.length > 0 && cats.length > 0;
  const run2 = action(runs[2] ? "Run lever 2 again" : "Run lever 2", async (_b, status) => {
    const cat = cats.find((c) => cardRef(c) === catSel.value);
    if (!cat) throw new Error("Choose a packaging catalogue");
    const currentPackagingPence = packCost.value.trim() === "" ? null : Math.round(Number(packCost.value) * 100);
    if (currentPackagingPence !== null && !(currentPackagingPence >= 0)) throw new Error("Packaging cost must be a number");
    status.textContent = "Loading parcels…";
    const parcels = await db.uploadParcels(upload.id);
    const rc = cards.map((c) => c.body);
    const r1 = runLever1(parcels, rc);
    const { results, summary } = runLever2(parcels, rc, cat.body, specs as ProductSpec[], { currentPackagingPence });
    const combined = combineLevers12(r1.results, results);
    await db.saveLeverRun({
      audit_id: audit.id, upload_id: upload.id, lever: 2, engine_version: ENGINE_VERSION, dataset_hash: upload.dataset_hash,
      rate_cards: cardsNow, packaging_catalogue: cardRef(cat), options: { currentPackagingPence, clearanceCm: 1 },
      summary: { ...summary, combined }, results: results.filter((r) => r.status === "saving").slice(0, MAX_STORED),
    });
    refresh();
  }, { primary: !runs[2], disabled: !l2Ready });

  // ---- Manual levers 3 to 5
  const manualCards = LEVERS.filter((l) => l.n >= 3).map((l) => {
    const m = manual.find((x) => x.lever === l.n);
    const amount = h("input", { type: "number", min: "0", step: "1", value: m?.annual_saving_pence != null ? String(m.annual_saving_pence / 100) : "", "aria-label": `Lever ${l.n} annual saving (£)` , class: "input-short" });
    const notes = h("textarea", { "aria-label": `Lever ${l.n} findings`, placeholder: "What was found, and how the saving was worked out" }, m?.summary ?? "");
    const save = action("Save", async (_b, status) => {
      const v = amount.value.trim() === "" ? null : Math.round(Number(amount.value) * 100);
      if (v !== null && !(v >= 0)) throw new Error("Saving must be a number");
      const entry: ManualLever = { lever: l.n as 3 | 4 | 5, annual_saving_pence: v, summary: notes.value };
      await db.saveManualLever(audit.id, entry);
      status.textContent = "Saved";
    });
    return h("div", { class: "lever" },
      h("h3", null, `Lever ${l.n}: ${l.name}`),
      h("p", { class: "muted small" }, l.what, " Worked by hand for now."),
      h("label", { class: "inline-field" }, "Annual saving £ ", amount),
      notes,
      h("div", { class: "row-actions" }, m?.updated_at ? h("span", { class: "muted small" }, `Last saved ${date(m.updated_at)}`) : "", save),
    );
  });

  box.append(
    h("p", { class: "muted small" }, "Rate cards used: ", cardsNow.length ? cardsNow.join(", ") : "none yet"),
    sample ? notice("warn", "Sample rate cards are in use. Results are for testing only.") : "",
    h("div", { class: "lever" },
      h("h3", null, `Lever 1: ${LEVERS[0].name}`),
      h("p", { class: "muted small" }, LEVERS[0].what),
      staleNotice(l1, upload, cardsNow),
      l1 ? lever1Results(l1, upload) : "",
      h("div", { class: "row-actions" }, l1?.created_at ? h("span", { class: "muted small" }, `Last run ${date(l1.created_at)} · engine ${l1.engine_version}`) : "", run1),
    ),
    h("div", { class: "lever" },
      h("h3", null, `Lever 2: ${LEVERS[1].name}`),
      h("p", { class: "muted small" }, LEVERS[1].what, " Single-product parcels are assessed automatically; mixed parcels are listed for manual review."),
      h("div", { class: "form-grid" },
        h("label", null, "Product sizes"),
        h("div", null, `${num(specs.length)} SKUs on file `, dropZone(specFile), " ", saveSpecs, " ", downloadLink("template", "product-sizes-template.csv", csvTemplate("products")), specOut),
        h("label", null, "Packaging catalogue"), cats.length ? catSel : notice("info", "Add a packaging catalogue first (Rate cards & packaging)."),
        h("label", null, "Current packaging cost"), h("div", null, "£ ", packCost, h("span", { class: "muted small" }, " per parcel, optional; gives a net saving")),
      ),
      !l1Current ? notice("info", "Run lever 1 on the current data first; lever 2 builds on its clean data.") : "",
      staleNotice(runs[2], upload, cardsNow),
      runs[2] ? lever2Results(runs[2], upload) : "",
      h("div", { class: "row-actions" }, runs[2]?.created_at ? h("span", { class: "muted small" }, `Last run ${date(runs[2].created_at)} · engine ${runs[2].engine_version}`) : "", run2),
    ),
    ...manualCards,
  );
  return box;
}

registerAuditSection(leversSection);
